import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { Office, OfficeState } from "../../models/office.model";
import { listOfficeData } from "../../redux/slices/RequestSlice";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { InputAdornment } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { clearSessionStorage, resetStateOnBoardingSlice, updateSelectedOffice } from "../../redux/slices/OnBoardingSlice";
import HomePageHeader from "../Onboarding/HomePageHeader";
import AuthFooter from "../../components/AuthFooter/AuthFooter";
import DoctorRecommendationLink from "../Onboarding/DoctorRecommendationLink";
import FormErrorMessage from "../../components/FormErrorMessage";
import "../../assets/css/styles.css";
import SearchIcon from "../../assets/images/search-icon.svg";
import "../../assets/css/patients.css";

export const NoDataFound = () => {
  return (
    <div className="search-row p-3 w-full border-b border-gray-300 cursor-pointer py-1">
      <p className="text-sm mb-1 text-slate-600"> No Data Found</p>
    </div>
  );
};

export const noData: Office[] = [
  {
    name: "No Data Found",
    street: "",
    createdDate: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: "",
    fax: "",
    contactName: "",
    contactEmail: "",
    officeState: OfficeState.Draft,
  },
];

export default function PatientSignupPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchTerm, setSearchTerm] = useState("");
  const [showList, setShowList] = useState(false);
  const [doctorNameError, setDoctorNameError] = useState("");
  const { officeList } = useSelector((state: RootState) => state.request);
  const params = new URLSearchParams(window.location.search);
  const signEmail = params.get("email");
  useEffect(() => {
    const email = new URLSearchParams(window.location.search).get("email")?.trim();

    email ? localStorage.setItem("signPatientEmail", email) : localStorage.removeItem("signPatientEmail");
  }, []);

  const validateDoctorName = (value: string): string => {
    // Must not be empty
    if (!value || !value.trim()) {
      return "Please enter your doctor's name to continue.";
    }

    const trimmedValue = value.trim();

    // Must be at least 2 characters to initiate search
    if (trimmedValue.length < 2) {
      return "Please enter at least 2 characters.";
    }

    // Maximum length: 100 characters
    if (trimmedValue.length > 100) {
      return "Please enter a valid doctor's name.";
    }

    // Allowed characters: Alphabets (A–Z, a–z), Spaces, Special symbols (@ # $ % ^ & * / | { } [ ] ~)
    // Allow: letters, spaces, and special symbols: @ # $ % ^ & * / | { } [ ] ~
    if (!/^[a-zA-Z\s@#$%^&*/|{}[\]~]+$/.test(trimmedValue)) {
      return "Please enter a valid doctor's name.";
    }

    return "";
  };

  const getFilteredData = (): Office[] | [] => {
    if (searchTerm.length <= 2) return [];

    const matchedFilteredData: Office[] = [];

    officeList.data?.content.forEach(el => {
      if (!el.primaryCareProviders || el.primaryCareProviders?.length === 0) {
        matchedFilteredData.push(el);
        return;
      }

      const drNameIncluded = el.primaryCareProviders?.filter(pcp => {
        const fullName = `${pcp.firstName?.toLowerCase()} ${pcp?.lastName?.toLowerCase()}`;
        const containsFirstName = pcp.firstName?.toLowerCase().includes(searchTerm.toLowerCase());
        const containsLastName = pcp?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
        const containsFullName = fullName.includes(searchTerm.toLowerCase());

        return containsFirstName || containsLastName || containsFullName;
      });

      const data = { ...el, primaryCareProviders: drNameIncluded };
      matchedFilteredData.push(data);
    });

    return matchedFilteredData;
  };

  const onOptionSelect = (officeData: Office) => {
    navigate("/onboarding/office", { state: officeData });
    dispatch(clearSessionStorage());
    dispatch(resetStateOnBoardingSlice());
    dispatch(updateSelectedOffice(officeData));
  };

  useEffect(() => {
    if (searchTerm.length !== 0) {
      // Validate input characters
      const error = validateDoctorName(searchTerm);
      if (error && error !== "Please enter at least 2 characters.") {
        setDoctorNameError(error);
        return;
      }

      if (searchTerm.length >= 2) {
        dispatch(listOfficeData({ searchTerm, page: 0, sortType: "", size: 100 }));
      }
    } else {
      setDoctorNameError("");
    }
  }, [searchTerm, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="auth-background">
      <HomePageHeader />
      <div className="relative w-full flex flex-grow justify-center overflow-hidden md:mt-16">
        <div className="relative z-10 flex items-center justify-center md:py-12 px-4">
          <div className="w-full max-w-[676px]">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white text-base hover:opacity-80 transition-opacity mb-6 -ml-1"
              aria-label="Go back"
            >
              <ArrowBackIcon sx={{ fontSize: 24 }} />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-[32px] font-bold text-white mb-7 text-center">NEW PATIENT SIGN UP</h1>

            <div className="bg-[#FFF5EF] rounded-3xl p-7 mb-7">
              <p className="mb-7 text-center">Type your doctor&apos;s name to get started creating your Docnote account</p>

              <div className="w-full">
                <Autocomplete
                  freeSolo={true}
                  className="w-full border border-navy-blue bg-white rounded-[100px]"
                  options={getFilteredData().length > 0 ? getFilteredData() : noData}
                  onInputChange={(e: React.SyntheticEvent<Element, Event>, value) => {
                    const inputValue = value ? value : "";
                    // Limit to 100 characters
                    const limitedValue = inputValue.length > 100 ? inputValue.slice(0, 100) : inputValue;
                    // Filter out invalid characters - only allow alphabets, spaces, and special symbols
                    const filteredValue = limitedValue.replace(/[^a-zA-Z\s@#$%^&*/|{}[\]~]/g, "");
                    setSearchTerm(filteredValue);
                    // Clear error when user starts typing
                    if (doctorNameError && filteredValue.length >= 2) {
                      setDoctorNameError("");
                    }
                  }}
                  disableClearable
                  openOnFocus
                  onFocus={() => setShowList(true)}
                  onBlur={() => {
                    setShowList(false);
                    // Validate on blur
                    const error = validateDoctorName(searchTerm);
                    if (error) {
                      setDoctorNameError(error);
                    }
                  }}
                  open={showList && searchTerm.length > 2}
                  includeInputInList={true}
                  filterOptions={x => x}
                  ListboxProps={{
                    className: "absolute left-0 top-13 w-full rounded-md bg-white border border-gray-300 shadow-lg z-50",
                    style: {
                      padding: 0,
                    },
                  }}
                  renderOption={(props, el: Office) =>
                    searchTerm.length > 2 ? (
                      <span className="hover:bg-gray-100" key={el.officeId + Math.random().toString()}>
                        {getFilteredData().length > 0 ? (
                          <div className="p-3 w-full border-b hover:bg-gray-50 cursor-pointer" onClick={() => onOptionSelect(el)}>
                            <p className="text-lg font-semibold mb-1">{el.name}</p>
                            <>
                              {el.primaryCareProviders?.map((pcp, ind) => {
                                return (
                                  <p key={ind} className="text-sm font-semibold mb-1">{` Dr. ${pcp.firstName} ${
                                    pcp?.lastName ? pcp?.lastName : ""
                                  }`}</p>
                                );
                              })}
                            </>
                            <p className="text-sm text-gray-600"> {[el.street, el.street1, el.city, el.state].filter(Boolean).join(", ")} </p>
                          </div>
                        ) : (
                          <NoDataFound />
                        )}
                      </span>
                    ) : null
                  }
                  renderInput={params => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          className="rounded-lg border-gray-300"
                          placeholder="Enter doctor's name"
                          type="search"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start" className="ml-3">
                                <img src={SearchIcon} alt="search" width={16} height={16} />
                              </InputAdornment>
                            ),
                          }}
                          inputProps={{
                            ...params.inputProps,
                            maxLength: 100,
                            style: {
                              fontSize: "16px",
                              padding: "2.5px 12px",
                              outline: "none",
                              boxShadow: "none",
                            },
                          }}
                        />
                      </div>
                    );
                  }}
                />
                {doctorNameError && (
                  <div className="mt-2">
                    <FormErrorMessage text={doctorNameError} />
                  </div>
                )}
              </div>
            </div>

            <div className="text-center">
              <p className="text-white mb-2">Your doctor not listed?</p>
              <p className="text-docnote-orange">
                <DoctorRecommendationLink />
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* <div className="mt-10 bg-[#111C3D] border-t border-white/20 ">
        <AuthFooter extraClasses={"footerContainer mx-auto"} />
      </div> */}
    </div>
  );
}
